import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import PeriodsTableView from 'components/PeriodsTableView'
import RootPeriodCard from 'components/RootPeriodCard'
import SimpleTitle from 'components/SimpleTitle'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import { addDays } from 'date-fns'
import dayjs from 'dayjs'
import { fetchWithToken, postWithTokenWithStatus } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { usePeriods } from 'hooks/usePeriods'
import { usePlanCounts } from 'hooks/usePlanCounts'
import type { FC } from 'react'
import { Suspense, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LockPeriodRequest, Period, ReopenPeriodRequest } from 'types/periods'
import { parsePeriodDate } from 'types/periods'
import { Permissions } from 'types/permissions'
import type { UserProfile } from 'types/user-profile'
import { checkPermission, getRootOrganizationId } from 'types/user-profile'
import PeriodsWizard from 'components/PeriodsWizard'

const ClosePeriods: FC = () => {
  const { token, profile } = useAuth()

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ClosePeriodsContent token={token!} profile={profile!} />
    </Suspense>
  )
}

function ClosePeriodsContent({ token, profile }: { token: string; profile: UserProfile }) {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRootPeriodId, setSelectedRootPeriodId] = useState<string>('')
  const [managerDataVisibilityDate, setManagerDataVisibilityDate] = useState<dayjs.Dayjs | null>(null)
  const [employeeDataVisibilityDate, setEmployeeDataVisibilityDate] = useState<dayjs.Dayjs | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false)
  const [periodToReopen, setPeriodToReopen] = useState<Period | null>(null)
  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  const [periodToLock, setPeriodToLock] = useState<Period | null>(null)
  const [lockedPeriodDialogOpen, setLockedPeriodDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  const [effectiveDates, setEffectiveDates] = useState<Record<string, string>>({})
  const theme = useTheme()

  const { periods: fetchedPeriods, isLoadingPeriods, handleRefetch: refetchPeriods } = usePeriods(token!, null, !!token)
  const { planCounts, isLoadingPlanCounts } = usePlanCounts(
    token!,
    getRootOrganizationId(profile),
    selectedRootPeriodId,
    !!token && !!selectedRootPeriodId
  )

  useEffect(() => {
    setIsLoading(isLoadingPeriods || isLoadingPlanCounts)
  }, [isLoadingPeriods, isLoadingPlanCounts])

  useEffect(() => {
    if (fetchedPeriods?.periods && fetchedPeriods.periods.length > 0 && !selectedRootPeriodId) {
      const firstPeriod = fetchedPeriods.periods[0]
      setSelectedRootPeriodId(firstPeriod.id || '')
    }
  }, [fetchedPeriods?.periods, selectedRootPeriodId])

  const rootPeriods = fetchedPeriods?.periods || []

  useEffect(() => {
    if (selectedRootPeriodId && rootPeriods.length > 0) {
      const period = rootPeriods.find((p) => p.id === selectedRootPeriodId)
      if (period) {
        try {
          // Parse period end date using existing utility
          const periodEndDate = parsePeriodDate(period.endDate)
          const dayAfterEnd = addDays(periodEndDate, 1)

          // Convert to dayjs objects
          const managerDate = period.managerDataVisibilityDate
            ? dayjs(parsePeriodDate(period.managerDataVisibilityDate))
            : dayjs(dayAfterEnd)

          const employeeDate = period.employeeDataVisibilityDate
            ? dayjs(parsePeriodDate(period.employeeDataVisibilityDate))
            : dayjs(dayAfterEnd)

          setManagerDataVisibilityDate(managerDate)
          setEmployeeDataVisibilityDate(employeeDate)
        } catch (error) {
          console.error('Error parsing dates:', error)
          setManagerDataVisibilityDate(null)
          setEmployeeDataVisibilityDate(null)
        }
      }
    }
  }, [selectedRootPeriodId, rootPeriods])

  // Fetch effective dates for the selected period
  useEffect(() => {
    const fetchEffectiveDates = async () => {
      if (!selectedRootPeriodId || !profile || !token) return

      const rootOrgId = getRootOrganizationId(profile)
      if (!rootOrgId) return

      try {
        const response = await fetchWithToken(
          `components/effective-dates?rootOrganizationID=${rootOrgId}&rootPeriodID=${selectedRootPeriodId}`,
          token,
          null
        )

        // Convert array to map of periodId -> latest effective date
        const datesMap: Record<string, string> = {}
        if (response.data && Array.isArray(response.data)) {
          response.data.forEach((item: { periodId: string; effectiveDate: string }) => {
            if (item.effectiveDate) {
              datesMap[item.periodId] = item.effectiveDate
            }
          })
        }
        setEffectiveDates(datesMap)
      } catch (error) {
        console.error('Error fetching effective dates:', error)
        setEffectiveDates({})
      } finally {
        // Loading handled automatically by fetchWithToken
      }
    }

    fetchEffectiveDates()
  }, [selectedRootPeriodId, profile, token])

  const selectedPeriod = rootPeriods.find((p) => p.id === selectedRootPeriodId)

  // Check if user has permission to update period visibility
  const canUpdateVisibility = profile ? checkPermission(profile, Permissions.PERIODS_VISIBILITY_UPDATE) : false

  // Helper function to check if a period or any of its descendants has future effective dates
  const hasFutureEffectiveDate = (
    period: Period
  ): { hasFuture: boolean; effectiveDate?: string; affectedPeriod?: Period } => {
    let latestFutureDate: Date | null = null
    let latestFutureDateString: string | null = null
    let affectedPeriod: Period | null = null

    const checkPeriodRecursively = (p: Period) => {
      // Check current period's effective date
      if (p.id && effectiveDates[p.id]) {
        const effectiveDate = effectiveDates[p.id]
        const effectiveDateObj = dayjs.utc(effectiveDate).toDate()
        const now = dayjs.utc().toDate()

        if (effectiveDateObj > now) {
          // This period has a future effective date
          if (!latestFutureDate || effectiveDateObj > latestFutureDate) {
            latestFutureDate = effectiveDateObj
            latestFutureDateString = effectiveDate
            affectedPeriod = p
          }
        }
      }

      // Recursively check all children
      if (p.children && p.children.length > 0) {
        p.children.forEach(checkPeriodRecursively)
      }
    }

    checkPeriodRecursively(period)

    if (latestFutureDate && latestFutureDateString && affectedPeriod) {
      return {
        hasFuture: true,
        effectiveDate: latestFutureDateString,
        affectedPeriod,
      }
    }

    return { hasFuture: false }
  }

  const findAllOpenDescendants = (period: Period): Period[] => {
    let openPeriods: Period[] = []
    if (!period.isClosed) {
      openPeriods.push(period)
    }
    if (period.children) {
      period.children.forEach((child) => {
        openPeriods = [...openPeriods, ...findAllOpenDescendants(child)]
      })
    }
    return openPeriods
  }

  // Helper function to format dayjs to standardized date format for API
  const formatDateToPeriodFormat = (date: dayjs.Dayjs): string => {
    return date.format('YYYY-MM-DD')
  }

  const handleClosePeriodClick = (period: Period) => {
    // Navigate to the new close period page
    navigate(`/admin/close-periods/${period.id}`)
  }

  const handleReopenPeriodClick = (period: Period) => {
    setPeriodToReopen(period)
    setReopenDialogOpen(true)
  }

  const handleLockPeriodClick = (period: Period) => {
    setPeriodToLock(period)
    setLockDialogOpen(true)
  }

  const handleConfirmReopen = () => {
    if (!periodToReopen || !token) return

    setIsSubmitting(true)
    const request: ReopenPeriodRequest = {
      rootOrganizationId: periodToReopen.organizationId || '',
      rootPeriodId: periodToReopen.rootPeriodId || '',
      periodsToOpen: [periodToReopen.id || ''],
    }

    postWithTokenWithStatus('periods/open', token, request)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(response.data.error || 'Failed to reopen period')
        }
        setSnackbar({
          open: true,
          message: 'Successfully reopened period',
          severity: 'success',
        })
        refetchPeriods()
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.message || 'An error occurred while reopening period',
          severity: 'error',
        })
      })
      .finally(() => {
        setIsSubmitting(false)
        setReopenDialogOpen(false)
        setPeriodToReopen(null)
      })
  }

  const findAllClosedDescendants = (period: Period): Period[] => {
    let closedPeriods: Period[] = []
    if (period.isClosed) {
      closedPeriods.push(period)
    }
    if (period.children) {
      period.children.forEach((child) => {
        closedPeriods = [...closedPeriods, ...findAllClosedDescendants(child)]
      })
    }
    return closedPeriods
  }

  const handleConfirmLock = () => {
    if (!periodToLock || !token) return

    setIsSubmitting(true)
    const request: LockPeriodRequest = {
      rootOrganizationId: periodToLock.organizationId || '',
      rootPeriodId: periodToLock.rootPeriodId || '',
      periodsToLock: [periodToLock.id || '', ...findAllClosedDescendants(periodToLock).map((p) => p.id || '')],
    }

    postWithTokenWithStatus('periods/lock', token, request)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(response.data.error || 'Failed to lock period')
        }
        setSnackbar({
          open: true,
          message: 'Successfully locked period',
          severity: 'success',
        })
        refetchPeriods()
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.message || 'An error occurred while locking period',
          severity: 'error',
        })
      })
      .finally(() => {
        setIsSubmitting(false)
        setLockDialogOpen(false)
        setPeriodToLock(null)
      })
  }

  const handleManagePlans = (period: Period) => {
    navigate(`/admin/plans?periodId=${period.id}&rootPeriodId=${selectedRootPeriodId}`)
  }

  const handleSaveDates = async () => {
    if (!canUpdateVisibility) return
    setConfirmDialogOpen(true)
  }

  const handleConfirmSaveDates = async () => {
    if (!token || !profile || !selectedPeriod) return

    setIsUpdating(true)
    setConfirmDialogOpen(false)

    try {
      const rootOrgId = getRootOrganizationId(profile)
      if (!rootOrgId) {
        throw new Error('Root organization ID not found')
      }

      const payload = {
        rootOrganizationId: rootOrgId,
        periodId: selectedRootPeriodId,
        managerDataVisibilityDate: managerDataVisibilityDate
          ? formatDateToPeriodFormat(managerDataVisibilityDate)
          : null,
        employeeDataVisibilityDate: employeeDataVisibilityDate
          ? formatDateToPeriodFormat(employeeDataVisibilityDate)
          : null,
      }

      const response = await postWithTokenWithStatus('periods/visibility', token, payload)

      if (response.status !== 200) {
        throw new Error(response.data?.error || 'Failed to update period visibility')
      }

      setSnackbar({
        open: true,
        message: 'Period visibility successfully updated',
        severity: 'success',
      })

      // Refresh periods data to get updated values
      refetchPeriods()
    } catch (error) {
      console.error('Error saving dates:', error)
      setSnackbar({
        open: true,
        message: 'Failed to update period visibility',
        severity: 'error',
      })
    } finally {
      setIsUpdating(false)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Grid container pb={2} spacing={2}>
        <Grid size={{ xs: 6 }}>
          <SimpleTitle title="Period Management" />
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: '300px', display: 'flex', alignItems: 'center', mb: 2 }}>
            <SinglePeriodSelector
              periods={rootPeriods}
              value={selectedRootPeriodId}
              onChange={setSelectedRootPeriodId}
              label="Select Period"
              rootPeriodOnly
              sortOrder="desc"
              variant="outlined"
              pill
              shrink
              labelBackgroundColor={theme.palette.background.default}
              disabled={isLoadingPeriods || rootPeriods.length === 0}
            />
          </Box>
          {!isLoadingPeriods && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PeriodsWizard
                rootOrganizationID={getRootOrganizationId(profile)!}
                existingPeriods={fetchedPeriods?.periods}
                refetchPeriods={refetchPeriods}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {selectedPeriod && (
        <Grid container spacing={3}>
          {/* Child Periods Table - Left Side */}
          <Grid size={{ xs: 8 }}>
            <PeriodsTableView
              periods={[selectedPeriod]}
              isLoading={isLoadingPeriods}
              showActions={true}
              onClosePeriod={handleClosePeriodClick}
              onReopenPeriod={handleReopenPeriodClick}
              onLockPeriod={handleLockPeriodClick}
              onLockedPeriodInfo={() => setLockedPeriodDialogOpen(true)}
              onManagePlans={handleManagePlans}
              isSubmitting={isSubmitting}
              effectiveDates={effectiveDates}
              hasFutureEffectiveDate={hasFutureEffectiveDate}
              planCounts={planCounts}
            />
          </Grid>

          {/* Root Period Card - Right Side */}
          <Grid size={{ xs: 4 }}>
            <RootPeriodCard
              period={selectedPeriod}
              showDataVisibilityControls={true}
              showActions={true}
              showVisibilityDates={true}
              managerDataVisibilityDate={managerDataVisibilityDate}
              employeeDataVisibilityDate={employeeDataVisibilityDate}
              isUpdating={isUpdating}
              isSubmitting={isSubmitting}
              canUpdateVisibility={canUpdateVisibility}
              onManagerDateChange={setManagerDataVisibilityDate}
              onEmployeeDateChange={setEmployeeDataVisibilityDate}
              onSaveDates={handleSaveDates}
              onClosePeriod={handleClosePeriodClick}
              onReopenPeriod={handleReopenPeriodClick}
              onLockPeriod={handleLockPeriodClick}
              onLockedPeriodInfo={() => setLockedPeriodDialogOpen(true)}
            />
          </Grid>
        </Grid>
      )}

      {!selectedPeriod && (
        <Box component={Paper} sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" mb={1}>
            No Periods Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No periods are available for closure management.
          </Typography>
        </Box>
      )}

      {/* Reopen Period Dialog */}
      <Dialog open={reopenDialogOpen} onClose={() => !isSubmitting && setReopenDialogOpen(false)}>
        <DialogTitle>Confirm Reopen Period</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to reopen the period "{periodToReopen?.label}"?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialogOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmReopen} disabled={isSubmitting} variant="contained">
            {isSubmitting ? <CircularProgress size={16} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock Period Dialog */}
      <Dialog open={lockDialogOpen} onClose={() => !isSubmitting && setLockDialogOpen(false)}>
        <DialogTitle>Confirm Lock Period</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to lock the period "{periodToLock?.label}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialogOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmLock} disabled={isSubmitting} variant="contained">
            {isSubmitting ? <CircularProgress size={16} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Locked Period Info Dialog */}
      <Dialog open={lockedPeriodDialogOpen} onClose={() => setLockedPeriodDialogOpen(false)}>
        <DialogTitle>Period is Locked</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This period has been locked and cannot be modified. Please contact support if you need to take any further
            actions on this period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockedPeriodDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for Data Visibility Update */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Data Visibility Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to update the data visibility dates for this period?
            <br />
            <br />
            <strong>
              This will affect which actual results and compensation progress are visible to managers and employees on
              dashboards and throughout the application.
            </strong>
            <br />
            <br />
            Data will only be visible to users after the specified visibility dates have passed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSaveDates} disabled={isUpdating} variant="contained">
            Update Visibility
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const LoadingSpinner: FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
    <CircularProgress />
  </Box>
)

export default ClosePeriods
